import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Copy, MessageCircle, RotateCcw, Settings2 } from "lucide-react";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../ui/alert-dialog";
import { api, formatApiError } from "../../../lib/api";
import { fillTemplate, withHeading, isDateField, placeholdersOf, prettyPhone, unresolvedIn, whatsappChatUrl } from "./feeUtils";
import { applyDetails, emptyRow, isReplaceTemplate, usableRows } from "./reminderMessage";
import PickList from "./PickList";
import DetailsPanel from "./DetailsPanel";

const input = "w-full h-11 px-3 rounded-lg border border-slate-300 focus:border-[#1E3A8A] outline-none text-sm bg-white";
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const fresh = () => ({ rows: [emptyRow()], fields: { currency: "INR" } });

function PlaceholderFields({ fields, values, setValues }) {
    if (!fields.length) return null;
    return (
        <div className="grid sm:grid-cols-2 gap-3">
            {fields.map((f) => (
                <label key={f} className={`block text-xs font-semibold text-slate-600 ${/message/i.test(f) ? "sm:col-span-2" : ""}`}>{f}
                    {/message/i.test(f) ? (
                        <textarea rows={4} value={values[f] || ""} data-testid={`fee-field-${slug(f)}`} onChange={(e) => setValues((v) => ({ ...v, [f]: e.target.value }))}
                            className="mt-1 w-full p-3 rounded-lg border border-slate-300 focus:border-[#1E3A8A] outline-none text-sm" />
                    ) : (
                        <input type={isDateField(f) ? "date" : "text"} value={values[f] || ""} data-testid={`fee-field-${slug(f)}`}
                            placeholder={/amount/i.test(f) ? "e.g. ₹5,000" : /link/i.test(f) ? "https://…" : f === "Parent Name" ? "Blank → Sir/Ma'am" : ""}
                            onChange={(e) => setValues((v) => ({ ...v, [f]: e.target.value }))} className={`${input} mt-1`} />
                    )}
                </label>
            ))}
        </div>
    );
}

export default function Composer({ parent, templates, categories, onManage, onSent }) {
    const [teaching, setTeaching] = useState("");
    const [tplId, setTplId] = useState("");
    const [greeting, setGreeting] = useState("");
    const [details, setDetails] = useState(fresh);
    const [showAll, setShowAll] = useState(false);
    const [options, setOptions] = useState(null);
    const [values, setValues] = useState({});
    const [message, setMessage] = useState("");
    const [edited, setEdited] = useState(false);
    const [followUp, setFollowUp] = useState("");
    const [confirm, setConfirm] = useState(false);
    const [sending, setSending] = useState(false);

    const pool = templates.filter((t) => !teaching || t.mode === "both" || t.mode === teaching);
    const tpl = pool.find((t) => t.id === tplId) || pool.find(isReplaceTemplate) || pool[0];
    const body = tpl?.body || "";
    const fields = useMemo(() => placeholdersOf(body), [body]);
    const typeGroups = useMemo(() => (categories || []).map((c) => ({
        label: c.label, items: pool.filter((t) => t.category === c.key).map((t) => ({ value: t.id, label: t.name, hint: t.purpose })),
    })), [categories, pool]);

    // New parent → clean slate (never carry details over from another parent or reminder).
    useEffect(() => {
        setDetails(fresh()); setShowAll(false); setGreeting(""); setFollowUp(""); setEdited(false); setOptions(null);
        setValues({ "Parent Name": parent?.name || "", "Student Name": parent?.student_name || "" });
        if (!parent?.id) return;
        let alive = true;
        api.get(`/admin/fee-reminders/parents/${parent.id}/options`).then(({ data }) => alive && setOptions(data)).catch(() => {});
        return () => { alive = false; };
    }, [parent?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const saveMaster = async (kind, value) => {
        try {
            const { data } = await api.post("/admin/fee-reminders/master-values", { kind, value });
            toast.success(data.existing ? `“${data.item.value}” is already in the master list` : `“${data.item.value}” saved to the master list`);
            if (parent?.id) {
                const res = await api.get(`/admin/fee-reminders/parents/${parent.id}/options`);
                setOptions(res.data);
            }
        } catch (err) { toast.error(`Couldn't save to the master list: ${formatApiError(err.response?.data?.detail)}`); }
    };

    const generated = useMemo(() => fillTemplate(applyDetails(body, {
        rows: details.rows, fields: details.fields, greeting, mode: isReplaceTemplate(tpl) ? "replace" : "insert",
        teaching: teaching || (tpl?.mode && tpl.mode !== "both" ? tpl.mode : ""),
    }), values), [body, tpl, details, greeting, teaching, values]);

    const lastTpl = useRef();
    useEffect(() => {
        const switched = lastTpl.current !== tpl?.id;
        lastTpl.current = tpl?.id;
        if (switched) setEdited(false);
        if (switched || !edited) setMessage(generated);
    }, [generated, tpl?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const missing = unresolvedIn(message);
    const finalText = withHeading(message);
    const reset = () => { setDetails(fresh()); setShowAll(false); setGreeting(""); setFollowUp(""); setEdited(false); setTplId(""); setTeaching(""); };
    const copy = async () => {
        try { await navigator.clipboard.writeText(finalText); toast.success("Message copied"); } catch { toast.error("Couldn't copy — please select and copy manually"); }
    };
    const sendLock = useRef(false);
    const send = async () => {
        setConfirm(false);
        if (sendLock.current) return;
        sendLock.current = true;
        setSending(true);
        window.open(whatsappChatUrl(parent.phone, finalText), "_blank", "noopener,noreferrer");
        try {
            await api.post("/admin/fee-reminders/log", {
                parent_id: parent.id, phone: parent.phone, template_id: tpl?.id || "", template_name: tpl?.name || "",
                template_category: tpl?.category || "", teaching_mode: teaching, greeting,
                details: usableRows(details.rows), amount: details.fields.amount || "", fields: details.fields,
                next_follow_up: followUp, message: finalText,
            });
            onSent?.();
        } catch (err) { toast.error(`WhatsApp opened, but the reminder wasn't logged: ${formatApiError(err.response?.data?.detail)}`); }
        setTimeout(() => { sendLock.current = false; setSending(false); }, 4000);
    };

    return (
        <section className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 space-y-4" data-testid="fee-composer">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <h3 className="font-heading font-bold text-[#0F172A]">2 · Choose Template &amp; Send</h3>
                <div className="flex gap-3">
                    <button type="button" onClick={reset} data-testid="fee-reset-form" className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-500 hover:underline">
                        <RotateCcw className="w-4 h-4" /> Reset form
                    </button>
                    <button type="button" onClick={onManage} data-testid="fee-manage-templates" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#1E3A8A] hover:underline">
                        <Settings2 className="w-4 h-4" /> Manage Templates
                    </button>
                </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
                <label className="block text-xs font-semibold text-slate-600">Teaching mode
                    <select value={teaching} onChange={(e) => setTeaching(e.target.value)} data-testid="fee-teaching-mode" className={`${input} mt-1`}>
                        <option value="">Any / not specified</option><option value="home">Home Tuition</option><option value="online">1:1 Live Online Classes</option>
                    </select>
                </label>
                <label className="block text-xs font-semibold text-slate-600">Greeting
                    <select value={greeting} onChange={(e) => setGreeting(e.target.value)} data-testid="fee-greeting" className={`${input} mt-1`}>
                        <option value="">As in template</option><option value="Sir/Ma'am">Dear Sir/Ma'am</option><option value="Ma'am">Dear Ma'am</option>
                        <option value="Sir">Dear Sir</option>{parent?.name && <option value={parent.name}>Dear {parent.name}</option>}
                    </select>
                </label>
            </div>
            <div className="block text-xs font-semibold text-slate-600">Reminder Type
                <div className="mt-1"><PickList testid="fee-reminder-type" groups={typeGroups} value={tpl?.id || ""} onChange={(v) => v && setTplId(v)} allowCustom={false} placeholder="Search reminder types" /></div>
                {/* kept for compatibility with existing flows/tests */}
                <select value={tpl?.id || ""} onChange={(e) => setTplId(e.target.value)} data-testid="fee-template-select" className="sr-only" aria-hidden tabIndex={-1}>
                    {pool.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
            </div>
            {tpl?.purpose && <p className="-mt-2 text-xs text-slate-500" data-testid="fee-template-purpose"><span className="font-semibold">Use when:</span> {tpl.purpose}</p>}
            <div className="border-t border-slate-100 pt-3">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Reminder details <span className="normal-case font-normal text-slate-400">— all optional; only what you select is included</span></div>
                <DetailsPanel state={details} setState={setDetails} options={options} visible={tpl?.fields || ["rows", "amount", "due_date"]} showAll={showAll} setShowAll={setShowAll} onSaveMaster={saveMaster} />
            </div>
            <PlaceholderFields fields={fields} values={values} setValues={setValues} />
            <label className="block text-xs font-semibold text-slate-600">
                <span className="flex items-center justify-between gap-2">Message preview (edit freely before sending)
                    {edited && (
                        <button type="button" onClick={() => { setEdited(false); setMessage(generated); }} data-testid="fee-message-reset" className="text-[#1E3A8A] font-bold hover:underline">
                            {generated !== message ? "Update with current details" : "Reset to template"}
                        </button>
                    )}
                </span>
                <textarea value={message} onChange={(e) => { setEdited(true); setMessage(e.target.value); }} rows={14} data-testid="fee-message"
                    className="mt-1 w-full p-3 rounded-lg border border-slate-300 focus:border-[#1E3A8A] outline-none text-sm leading-relaxed bg-[#F8FAFC]" />
            </label>
            {edited && generated !== message && (
                <p className="text-xs text-slate-500 -mt-2" data-testid="fee-edited-note">You edited the message, so detail changes are not applied automatically. Use “Update with current details” to regenerate.</p>
            )}
            {missing.length > 0 && (
                <p className="flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="fee-unresolved-warning">
                    <AlertTriangle className="w-4 h-4 shrink-0" /> Still to fill: {missing.join(", ")}
                </p>
            )}
            <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
                <label className="block text-xs font-semibold text-slate-600">Next follow-up date <span className="font-normal text-slate-400">(optional, saved in history only)</span>
                    <input type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} data-testid="fee-next-follow-up" className={`${input} mt-1`} />
                </label>
                <button type="button" onClick={copy} disabled={!message.trim()} data-testid="fee-copy-message"
                    className="h-11 px-4 rounded-lg border border-slate-300 text-sm font-bold inline-flex items-center justify-center gap-2 hover:border-[#1E3A8A] disabled:opacity-50">
                    <Copy className="w-4 h-4" /> Copy message
                </button>
            </div>
            <button type="button" onClick={() => setConfirm(true)} disabled={!parent || !message.trim() || sending} data-testid="fee-send-whatsapp"
                className="w-full h-12 rounded-xl bg-[#25D366] hover:bg-[#1ebe5a] text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                <MessageCircle className="w-5 h-5" /> {sending ? "Opened in WhatsApp…" : parent ? `Send on WhatsApp to ${prettyPhone(parent.phone)}` : "Select a parent to send"}
            </button>
            <p className="text-xs text-slate-500">Opens WhatsApp on this device with the chat and message ready — you press Send. Sending a reminder never changes payments, class status or tutor assignments.</p>
            <AlertDialog open={confirm} onOpenChange={setConfirm}>
                <AlertDialogContent className="z-[70] w-[calc(100%-2rem)] rounded-xl" data-testid="fee-send-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Send this reminder?</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-1 text-sm text-slate-600">
                                <div>Parent: <b data-testid="fee-confirm-parent">{parent?.name}</b> ({prettyPhone(parent?.phone)})</div>
                                <div>Reminder type: <b data-testid="fee-confirm-type">{tpl?.name}</b></div>
                                {missing.length > 0 && (
                                    <div className="text-amber-800" data-testid="fee-unresolved-dialog">Still contains {missing.join(", ")} — go back to fill, or send anyway.</div>
                                )}
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="fee-unresolved-back" className="bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 hover:text-slate-900">Go back</AlertDialogCancel>
                        <AlertDialogAction data-testid="fee-unresolved-continue" onClick={send}>Open WhatsApp</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </section>
    );
}
